// TODO - update filename to arrayUtils.ts to better capture file content

/**
 * Efficiently moves an item from one index to another in an array.
 *
 * @param array - The source array
 * @param from - The index of the item to move
 * @param to - The target index where the item should be moved
 * @returns A new array with the item moved, or the same reference if no change
 */
export function arrayMove<T>(array: T[], from: number, to: number): T[] {
  // Return same reference for no-op cases
  if (
    from === to ||
    from < 0 ||
    from >= array.length ||
    to < 0 ||
    to >= array.length
  ) {
    return array;
  }

  // create a shallow copy (immutability)
  const result = array.slice();

  const [item] = result.splice(from, 1); // Remove item from original position
  result.splice(to, 0, item); // Insert item at new position

  return result;
}

/**
 * Immutably removes an item from an array at a given index.
 *
 * @param array - The source array
 * @param index - The index of the item to remove
 * @returns A new array with the item removed, or the same reference if no change
 */
export function arrayRemove<T>(array: T[], index: number): T[] {
  // Return same reference for no-op cases
  if (index < 0 || index > array.length) {
    return array;
  }

  // create a shallow copy (immutability)
  const result = array.slice();

  result.splice(index, 1);
  return result;
}

/**
 * Immutably inserts an item into an array at a given index.
 *
 * @param array - The source array
 * @param item - The item to insert
 * @param index - The index where the item should be inserted
 * @returns A new array with the item inserted, or the same reference if no change
 */
export function arrayInsert<T>(
  array: T[],
  item: T,
  insertDir: 'before' | 'after',
  index: number
): T[] {
  // Return same reference for no-op cases
  if (index < 0 || index > array.length) {
    return array;
  }

  // create a shallow copy (immutability)
  const result = array.slice();

  // When target is the last item, insert after it; otherwise insert before
  const insertIndex = insertDir === 'before' ? Math.max(0, index) : index + 1;
  result.splice(insertIndex, 0, item);

  return result;
}
